
import React from 'react';
import { ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent } from '../../../ui/context-menu';
import { ZOOM_PRESETS } from '../../../../types';


interface TimelineItemContextMenuProps {
  onDuplicate?: () => void;
  onDelete?: () => void;
  onSplit?: () => void; // Add split handler
  onMute?: () => void; // Mute handler
  onZoom?: (scale: number) => void; // Zoom handler
  onDuplicateItems?: (itemIds: string[]) => void;
  onDeleteItems?: (itemIds: string[]) => void;
  onSplitItems?: (itemId: string, splitTime: number) => void; // Add split items handler
  onMuteItems?: (itemIds: string[]) => void; // Mute items handler
  onZoomItems?: (itemIds: string[], scale: number) => void; // Zoom items handler
  onToggleAudioOnly?: () => void; // Audio only toggle handler
  isSelected?: boolean;
  selectedItemIds?: string[];
  duplicateText: string;
  deleteText: string;
  showSplit?: boolean; // Whether to show the split option (only for single items)
  itemType?: string; // Item type to check if it's a video
  isMuted?: boolean; // Current mute state
  isVideoItem?: boolean; // Whether this is a video item
  isAudioOnly?: boolean; // Whether video is in audio-only mode
  currentFrame?: number; // Current playhead frame position
  fps?: number; // Frames per second for time conversion
}

export const TimelineItemContextMenu: React.FC<TimelineItemContextMenuProps> = ({
  onDuplicate,
  onDelete,
  onSplit,
  onMute,
  onZoom,
  onDuplicateItems,
  onDeleteItems,
  onSplitItems,
  onMuteItems,
  onZoomItems,
  onToggleAudioOnly,
  duplicateText,
  deleteText,
  showSplit = false,
  isMuted = false,
  isVideoItem = false,
  isAudioOnly = false,
  selectedItemIds = [],
  currentFrame,
  fps,
}) => {
  // Determine mute text based on current state and selection
  const isMultiSelection = selectedItemIds.length > 1;
  const muteText = isMuted 
    ? (isMultiSelection ? `Unmute ${selectedItemIds.length} items` : 'Unmute')
    : (isMultiSelection ? `Mute ${selectedItemIds.length} items` : 'Mute');

  // Zoom preset options
  const PRESET_OPTIONS = [
    { key: 'default', label: 'Default (1x)', scale: ZOOM_PRESETS.default, color: 'bg-gray-400' },
    { key: 'shallow', label: 'Shallow (1.5x)', scale: ZOOM_PRESETS.shallow, color: 'bg-blue-400' },
    { key: 'moderate', label: 'Moderate (2x)', scale: ZOOM_PRESETS.moderate, color: 'bg-purple-400' },
    { key: 'maximum', label: 'Maximum (3x)', scale: ZOOM_PRESETS.maximum, color: 'bg-red-400' },
  ];

  return (
    <ContextMenuContent className="w-48">
      <ContextMenuItem 
        onClick={onDuplicate} 
        disabled={!onDuplicateItems}
      >
        <span>{duplicateText}</span>
      </ContextMenuItem>
      
      {/* Show mute option only for video items */}
      {isVideoItem && onMute && onMuteItems && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={onMute}>
            <span>{muteText}</span>
          </ContextMenuItem>
        </>
      )}

      {/* Audio only toggle for video items */}
      {isVideoItem && onToggleAudioOnly && (
        <ContextMenuItem onClick={onToggleAudioOnly}>
          <span>{isAudioOnly ? 'Show Video' : 'Audio Only'}</span>
        </ContextMenuItem>
      )}

      {/* Show zoom option only for video items */}
      {isVideoItem && onZoom && onZoomItems && (
        <>
          <ContextMenuSeparator />
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <span>Zoom Level</span>
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="w-40">
              {PRESET_OPTIONS.map((preset) => (
                <ContextMenuItem
                  key={preset.key}
                  onClick={() => onZoom(preset.scale)}
                >
                  <div className={`w-3 h-3 rounded-sm ${preset.color} mr-2`} />
                  {preset.label}
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        </>
      )}
      
      {showSplit && onSplit && onSplitItems && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={onSplit}>
            <span>Split at playhead</span>
          </ContextMenuItem>
        </>
      )}
      
      <ContextMenuSeparator />
      <ContextMenuItem 
        onClick={onDelete} 
        disabled={!onDeleteItems}
        className="text-red-600 focus:text-red-600"
      >
        <span>{deleteText}</span>
      </ContextMenuItem>
    </ContextMenuContent>
  );
}; 